import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Shield, Star, Award, Clock, Accessibility } from 'lucide-react';
import TrustpilotWidget from './TrustpilotWidget';

const Footer = () => {
  return (
    <footer className="bg-foreground text-white">
      {/* Trust Badges Bar */}
      <div className="bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-white">TÜV-geprüft</p>
                <p className="text-white/60 text-sm">Zertifizierte Qualität</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Star className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="font-semibold text-white">4.9/5 Sterne</p>
                <p className="text-white/60 text-sm">Kundenbewertung</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Award className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="font-semibold text-white">Zugelassen</p>
                <p className="text-white/60 text-sm">§ 126 SGB V</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-white">Schnelle Lieferung</p>
                <p className="text-white/60 text-sm">Innerhalb 3-5 Tage</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <span className="text-4xl text-white font-serif" style={{ fontFamily: "'Times New Roman', Georgia, serif" }}>π</span>
              <span className="text-xl font-bold font-[Manrope]">Medical</span>
            </div>
            <p className="text-white/70 leading-relaxed mb-4">
              Ihre kostenlose Pflegebox - jeden Monat direkt nach Hause. 
              Wir übernehmen die Abrechnung mit Ihrer Krankenkasse.
            </p>
            <a 
              href="tel:+4952425835480" 
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-3 rounded-xl font-semibold transition-colors"
            >
              <Phone className="w-5 h-5" />
              05242 583 5480
            </a>
            <p className="text-white/50 text-sm mt-2">Mo–Fr 8:00–20:00 Uhr</p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-6 font-[Manrope]">Schnellzugriff</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/antrag" className="text-white/70 hover:text-white transition-colors">
                  Meine Pflegebox
                </Link>
              </li>
              <li>
                <Link to="/pflegegrad-rechner" className="text-white/70 hover:text-white transition-colors">
                  Pflegegrad-Rechner
                </Link>
              </li>
              <li>
                <Link to="/anspruch-pruefen" className="text-white/70 hover:text-white transition-colors">
                  Anspruch prüfen
                </Link>
              </li>
              <li>
                <Link to="/ratgeber" className="text-white/70 hover:text-white transition-colors">
                  Ratgeber
                </Link>
              </li>
              <li>
                <Link to="/ueber-uns" className="text-white/70 hover:text-white transition-colors">
                  Über uns
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-bold text-lg mb-6 font-[Manrope]">Rechtliches</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/impressum" className="text-white/70 hover:text-white transition-colors">
                  Impressum
                </Link>
              </li>
              <li>
                <Link to="/datenschutz" className="text-white/70 hover:text-white transition-colors">
                  Datenschutz
                </Link>
              </li>
              <li>
                <Link to="/agb" className="text-white/70 hover:text-white transition-colors">
                  AGB
                </Link>
              </li>
              <li>
                <Link to="/widerruf" className="text-white/70 hover:text-white transition-colors">
                  Widerrufsrecht
                </Link>
              </li>
              <li>
                <Link to="/barrierefreiheit" className="text-white/70 hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-barrierefreiheit-link">
                  <Accessibility className="w-3.5 h-3.5" />
                  Barrierefreiheit
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-white/70 hover:text-white transition-colors">
                  FAQ / Häufige Fragen
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-lg mb-6 font-[Manrope]">Wir sind für Sie da</h3>
            <ul className="space-y-4">
              <li>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold transition-colors"
                  data-testid="footer-kontakt-link"
                >
                  <Mail className="w-4 h-4" />
                  Kontakt aufnehmen
                </Link>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-white/70 text-sm">Telefon</p>
                  <a href="tel:+4952425835480" className="text-white hover:text-primary transition-colors font-semibold">
                    05242 583 5480
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-white/70 text-sm">E-Mail</p>
                  <a href="mailto:info@medicalpi.de" className="text-white hover:text-primary transition-colors">
                    info@medicalpi.de
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-white/70 text-sm">Adresse</p>
                  <p className="text-white">
                    Josef F. und Benjamin A. GbR<br />
                    Hauptstraße 67<br />
                    33378 Rheda-Wiedenbrück
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Trustpilot / Bewertungen Slot */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-white/60 text-sm">
              <span className="font-semibold text-white">Geprüfte Qualität:</span> DSGVO-konform · § 126 SGB V zugelassen · Versand mit DHL
            </div>
            <TrustpilotWidget compact />
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} Josef F. und Benjamin A. GbR. Alle Rechte vorbehalten.
          </p>
          <div className="flex items-center gap-6">
            <span className="trust-badge bg-white/10 text-white/90">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              </svg>
              DSGVO-konform
            </span>
            <span className="trust-badge bg-white/10 text-white/90">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              IK-Nummer registriert
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
